import { IChartConfig } from '@customComp/Chart/_config/_interface';

type TTooltipExtraItem = NonNullable<NonNullable<IChartConfig['tooltipSetting']>['tooltipExtra']>[number];

const getFieldKey = (item: TTooltipExtraItem): string => {
  if (item?.key) {
    return item.key;
  }
  if (item?.tableCode && item?.column) {
    return `${item.tableCode}_${item.column}`;
  }
  return (item?.value || '').replace('.', '_');
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** 转义后按行拆分，用 <br /> 保留换行（\n / \r\n / \r）；去掉开头多出的一个换行 */
const escapeHtmlPreserveNewlines = (value: string): string =>
  value
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/^\n/, '')
    .split('\n')
    .map((line) => escapeHtml(line))
    .join('<br />');

const normalizeText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '-';
  }
  const text = String(value).trim();
  return text || '-';
};

const getFieldValues = (
  item: TTooltipExtraItem,
  tooltipExtraMap: Record<string, string[][]>,
  dataIndex: number,
): string[] => {
  const key = getFieldKey(item);
  const rawValues = tooltipExtraMap?.[key]?.[dataIndex] || [];
  const valueSet = new Set<string>(
    rawValues.map((rawValue) => normalizeText(rawValue)),
  );
  if (!valueSet.size) {
    valueSet.add('-');
  }
  return Array.from(valueSet);
};

export const buildTooltipExtraContentHtml = (
  tooltipSetting?: IChartConfig['tooltipSetting'],
  tooltipExtraMap: Record<string, string[][]> = {},
  dataIndex = 0,
): string => {
  const extraItems = tooltipSetting?.tooltipExtra || [];
  const validItems = extraItems.filter((item) => {
    if (item?.type === 'text') {
      return !!item?.value?.trim?.();
    }
    return item?.type === 'field' && !!item?.value;
  });
  if (!validItems?.length) {
    return '';
  }
  const rows = validItems
    .map((item) => {
      if (item.type === 'text') {
        return `<div class="dbl-tooltip-extra-item dbl-tooltip-extra-item-text">${escapeHtmlPreserveNewlines(item.value)}</div>`;
      }
      const values = getFieldValues(item, tooltipExtraMap, dataIndex);
      const valueHtml = values?.map(
        (value) => `<div class="dbl-tooltip-extra-field-value-item">${escapeHtml(value)}</div>`,
      )?.join('') || '';
      return `<div class="dbl-tooltip-extra-item">
        <div class="dbl-tooltip-extra-field-value">${valueHtml}</div>
      </div>`;
    })
    .join('');
  return `<div class="dbl-tooltip-extra-content">
    <div class="dbl-tooltip-extra-divider"></div>
    ${rows}
  </div>`;
};

export const appendTooltipExtraContent = ({
  html,
  tooltipExtraMap,
  dataIndex,
  tooltipSetting,
}: {
  html: string;
  tooltipExtraMap?: Record<string, string[][]>;
  dataIndex?: number;
  tooltipSetting?: IChartConfig['tooltipSetting'];
}): string => {
  const extraHtml = buildTooltipExtraContentHtml(
    tooltipSetting,
    tooltipExtraMap,
    dataIndex,
  );
  if (!extraHtml) {
    return html || '';
  }
  if (!html) {
    return extraHtml;
  }
  const tableCloseTag = '</table>';
  if (html.includes(tableCloseTag)) {
    return html.replace(tableCloseTag, `${tableCloseTag}${extraHtml}`);
  }
  const lastDivIndex = html.lastIndexOf('</div>');
  if (lastDivIndex > -1) {
    return `${html.slice(0, lastDivIndex)}${extraHtml}${html.slice(lastDivIndex)}`;
  }
  return `${html}${extraHtml}`;
};
